import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLock } from '../context/LockContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

export default function LockScreen() {
  const { t } = useLanguage();
  const { pinSessions, unlock, clearAllPins } = useLock();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);

  const tryUnlock = (fullPin) => {
    setSubmitting(true);
    setTimeout(() => {
      const ok = unlock(fullPin);
      if (!ok) {
        setShake(true);
        setError(true);
        setTimeout(() => {
          setPin('');
          setShake(false);
          setError(false);
        }, 600);
      }
      setSubmitting(false);
    }, 150);
  };

  const press = (digit) => {
    if (submitting || pin.length >= 4) return;
    const next = pin + digit;
    setPin(next);
    setError(false);
    if (next.length === 4) tryUnlock(next);
  };

  const backspace = () => {
    if (submitting) return;
    setPin(p => p.slice(0, -1));
    setError(false);
  };

  const handleForgotPin = () => {
    clearAllPins();
    logout();
    navigate('/login');
  };

  // ESC kapatma — confirm modal açıkken geri dön
  useEffect(() => {
    if (!confirmLogout) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') setConfirmLogout(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [confirmLogout]);

  const noPin = pinSessions.length === 0;

  // PIN ayarlanmamış → basit bypass ekranı
  if (noPin) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="lock-screen-title-nopin"
        className="fixed inset-0 z-[200] bg-gradient-to-b from-gray-900 to-gray-950 flex flex-col items-center justify-center select-none">
        <div className="text-6xl mb-4" aria-hidden="true">🔒</div>
        <h2 id="lock-screen-title-nopin" className="text-white text-2xl font-bold mb-2">{t('lockScreenTitle') || 'Ekran Kilitli'}</h2>
        <p className="text-amber-400 text-sm mb-8 text-center px-8">
          {t('lockScreenNoPin') || 'Henüz PIN ayarlanmamış.'}<br />{t('lockScreenNoPinHint') || 'Devam etmek için butona dokunun.'}
        </p>
        <button
          onClick={() => unlock('')}
          className="px-8 py-4 bg-blue-600 active:bg-blue-800 text-white text-base font-bold rounded-2xl transition-all active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900">
          {t('lockScreenUnlock') || 'Kilidi Aç'}
        </button>
        <p className="text-gray-500 text-xs mt-6 text-center px-8">{t('lockScreenPinFromSettings') || "⚙️ Ayarlar'dan PIN belirleyebilirsiniz"}</p>
      </div>
    );
  }

  // PIN unutma onay ekranı
  if (confirmLogout) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="lock-screen-reset-title"
        aria-describedby="lock-screen-reset-desc"
        className="fixed inset-0 z-[200] bg-gradient-to-b from-gray-900 to-gray-950 flex flex-col items-center justify-center select-none px-8">
        <div className="text-5xl mb-6" aria-hidden="true">⚠️</div>
        <h2 id="lock-screen-reset-title" className="text-white text-xl font-bold mb-3 text-center">{t('lockScreenResetTitle') || "PIN'i Sıfırla"}</h2>
        <p id="lock-screen-reset-desc" className="text-gray-400 text-sm text-center mb-8 leading-relaxed">
          {t('lockScreenResetDesc') || "Tüm kayıtlı PIN'ler silinecek ve oturumunuz kapatılacak. Tekrar giriş yaparak yeni PIN belirleyebilirsiniz."}
        </p>
        <button
          onClick={handleForgotPin}
          className="w-full max-w-xs py-4 bg-red-600 active:bg-red-800 text-white font-bold rounded-2xl mb-3 transition-all active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400">
          {t('lockScreenResetConfirm') || 'Evet, Çıkış Yap ve Sıfırla'}
        </button>
        <button
          onClick={() => setConfirmLogout(false)}
          className="w-full max-w-xs py-3 bg-gray-700 active:bg-gray-600 text-gray-300 font-medium rounded-2xl transition-all active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400">
          {t('lockScreenResetBack') || 'Geri Dön'}
        </button>
      </div>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="lock-screen-title"
      className="fixed inset-0 z-[200] bg-gradient-to-b from-gray-900 to-gray-950 flex flex-col items-center justify-center select-none overflow-hidden">
      {/* Top icon */}
      <div className="mb-10 text-center">
        <div className={`text-6xl mb-4 transition-all ${shake ? 'animate-shake' : ''}`} aria-hidden="true">
          {error ? '🔴' : '🔒'}
        </div>
        <h2 id="lock-screen-title" className="text-white text-2xl font-bold tracking-tight">{t('lockScreenTitle') || 'Ekran Kilitli'}</h2>
        {pinSessions.length === 1 && (
          <p className="text-gray-400 text-sm mt-2">{pinSessions[0].userName}</p>
        )}
        {pinSessions.length > 1 && (
          <p className="text-gray-400 text-sm mt-2">{t('lockScreenEnterPin') || 'PIN kodunuzu girin'}</p>
        )}
      </div>

      {/* PIN dots */}
      <div
        className={`flex gap-5 mb-10 ${shake ? 'animate-shake' : ''}`}
        role="status"
        aria-live="polite"
        aria-label={t('lockScreenEnterPin') || 'PIN kodunuzu girin'}>
        {[0, 1, 2, 3].map(i => (
          <div
            key={i}
            className={`w-5 h-5 rounded-full border-2 transition-all duration-150 ${
              pin.length > i
                ? error ? 'bg-red-500 border-red-400 scale-110' : 'bg-blue-400 border-blue-300 scale-110'
                : 'bg-transparent border-gray-500'
            }`}
          />
        ))}
      </div>

      {/* Number pad */}
      <div className="grid grid-cols-3 gap-3 mb-3 w-72">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
          <button
            key={n}
            onClick={() => press(String(n))}
            aria-label={`${t('lockScreenDigit') || 'Rakam'} ${n}`}
            aria-busy={submitting}
            className="h-20 rounded-2xl bg-gray-800/70 active:bg-gray-600 border border-gray-700/50 text-white text-2xl font-semibold transition-all active:scale-90 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          >
            {n}
          </button>
        ))}
        <div />
        <button
          onClick={() => press('0')}
          aria-label={`${t('lockScreenDigit') || 'Digit'} 0`}
          aria-busy={submitting}
          className="h-20 rounded-2xl bg-gray-800/70 active:bg-gray-600 border border-gray-700/50 text-white text-2xl font-semibold transition-all active:scale-90 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        >
          0
        </button>
        <button
          onClick={backspace}
          aria-label={t('lockScreenBackspace') || 'Geri sil'}
          className="h-20 rounded-2xl bg-gray-700/40 active:bg-gray-600/60 border border-gray-700/30 text-gray-300 text-2xl transition-all active:scale-90 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        >
          <span aria-hidden="true">⌫</span>
        </button>
      </div>

      {/* Error / forgot PIN */}
      <div className="flex flex-col items-center gap-3 mt-2">
        {error && (
          <p role="alert" className="text-red-400 text-sm font-medium">{t('lockScreenWrongPin') || 'Yanlış PIN kodu'}</p>
        )}
        <button
          onClick={() => setConfirmLogout(true)}
          className="text-gray-500 text-sm underline active:text-gray-300 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded">
          {t('lockScreenForgotPin') || "PIN'imi unuttum"}
        </button>
      </div>

      {/* Registered user avatars (multi-user hint) */}
      {pinSessions.length > 1 && (
        <div className="mt-8 flex gap-4">
          {pinSessions.map(s => (
            <div key={s.userId} className="text-center">
              <div className="w-12 h-12 rounded-full bg-gray-700 border border-gray-600 flex items-center justify-center text-lg font-bold text-white mx-auto mb-1.5">
                {s.userName?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <p className="text-gray-400 text-xs font-medium">{s.userName?.split(' ')[0]}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
